import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";
import { ExternalLink, LifeBuoy } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { cn } from "../../utils/cn";
import useAuth from "../../hooks/useAuth";
import { safeImageUrl } from "../announcements/RemoteAnnouncementContent";
import { getNewsFeed, sendNewsReceipts } from "../../api/productNews";
import NewsContent, { CategoryBadge, formatNewsDate } from "./NewsContent";
import { primaryActionOf } from "./commerceMenu";
import { markMenuPathVisited, setNewsUnread, useNewsSummary } from "./newsSummaryStore";
import { ctaTargetOf, unseenIdsOf } from "./newsRules";

// ─────────────────────────────────────────────────────────────────────────────
// Campana del encabezado para el COMERCIO: novedades de la plataforma con
// contador de no leídas. Se muestran con la marca que el comercio ya ve en su
// portal (la de su operador si tiene co-branding), que resuelve el backend.
//
// Abrir el panel marca como vistas las novedades que aparecen en él; abrir el
// detalle y tocar el botón se registran aparte (métricas del editor). Todo es
// best-effort: si el backend no responde, la campana queda sin contador.
//
// La campana está montada en todas las pantallas del comercio, así que también
// es quien avisa que el usuario entró a una pantalla que el menú marcaba como
// "Nuevo" (ver newsSummaryStore).
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

function BrandLine({ brand }) {
  const { t } = useTranslation();
  if (brand?.kind === "operator") {
    const logo = safeImageUrl(brand.logo);
    return (
      <span className="flex min-w-0 items-center gap-2 text-xs font-normal text-fg-muted">
        {logo && (
          <img src={logo} alt="" className="dark-safe-img h-5 w-5 shrink-0 rounded object-contain" />
        )}
        <span className="truncate">{brand.name ? t("De {{name}}", { name: brand.name }) : null}</span>
      </span>
    );
  }
  if (brand?.kind === "beartrack") {
    return <span className="text-xs font-normal text-fg-muted">{t("De Beartrack")}</span>;
  }
  return null;
}

/** "Más ayuda": ruta del portal con el router, o link externo en otra pestaña. */
function HelpLink({ help, onNavigate }) {
  const target = help ? ctaTargetOf(help.url) : null;
  if (!target) return null;
  const className =
    "mr-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";
  const content = (
    <>
      <LifeBuoy className="h-4 w-4" aria-hidden="true" />
      {help.label}
    </>
  );
  return target.kind === "external" ? (
    <a href={target.href} target="_blank" rel="noopener noreferrer nofollow" className={className}>
      {content}
    </a>
  ) : (
    <button type="button" onClick={() => onNavigate(target.path)} className={className}>
      {content}
    </button>
  );
}

function NewsDetail({ news, onClose, onCta, onNavigate }) {
  const { t } = useTranslation();
  const action = primaryActionOf(news, t);
  const target = action ? ctaTargetOf(action.url) : null;
  return (
    <Modal
      open={Boolean(news)}
      onClose={onClose}
      size="md"
      title={news?.title}
      description={news?.summary || undefined}
      footer={
        <div className="flex w-full flex-wrap items-center justify-end gap-2">
          <HelpLink help={news?.help} onNavigate={onNavigate} />
          <Button variant="ghost" onClick={onClose}>
            {t("Cerrar")}
          </Button>
          {target?.kind === "external" && (
            <a
              href={target.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => onCta(news, target)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              {action.label}
            </a>
          )}
          {target?.kind === "internal" && (
            <Button onClick={() => onCta(news, target)}>{action.label}</Button>
          )}
        </div>
      }
    >
      {news && <NewsContent news={news} />}
    </Modal>
  );
}

export default function NewsBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const summary = useNewsSummary(user?.id ?? null);
  const unread = summary.unread;
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ status: "idle", brand: null, items: [], total: 0, page: 0 });
  const [detail, setDetail] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Entró a una pantalla que el menú marcaba como "Nuevo": se apaga.
  useEffect(() => {
    if (summary.status === "ready") markMenuPathVisited(pathname);
  }, [pathname, summary.status]);

  /** Carga una página; lo nuevo que aparece queda como visto. */
  const loadPage = useCallback(async (page) => {
    setFeed((f) => ({ ...f, status: "loading" }));
    const data = await getNewsFeed({ page, limit: PAGE_SIZE });
    if (!mounted.current) return;
    if (!data) {
      setFeed((f) => ({ ...f, status: "error" }));
      return;
    }
    setFeed((f) => ({
      status: "ready",
      brand: data.brand ?? null,
      items: page === 1 ? data.items : [...f.items, ...data.items],
      total: data.total ?? data.items.length,
      page,
    }));
    const unseen = unseenIdsOf(data.items);
    // Lo que no está en las páginas abiertas sigue contando como no leído.
    setNewsUnread((data.unread ?? 0) - unseen.length);
    if (unseen.length > 0) {
      sendNewsReceipts(unseen.map((id) => ({ id, event: "seen" })));
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadPage(1);
  };

  const openDetail = (news) => {
    setOpen(false);
    setDetail(news);
    sendNewsReceipts([{ id: news.id, event: "opened" }]);
  };

  const onCta = (news, target) => {
    sendNewsReceipts([{ id: news.id, event: "cta_clicked" }]);
    if (target.kind === "internal") {
      setDetail(null);
      navigate(target.path);
    }
  };

  const goTo = (path) => {
    setDetail(null);
    navigate(path);
  };

  const label =
    unread > 0
      ? t("Novedades ({{n}} sin leer)", { n: unread })
      : t("Novedades");

  return (
    <>
      <Dropdown isOpen={open} toggle={toggle} className="header-notif">
        <DropdownToggle
          tag="button"
          caret={false}
          className="header-icon-btn"
          aria-label={label}
          title={t("Novedades")}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            notifications
          </span>
          {unread > 0 && (
            <span className="header-notif__badge">{unread > 9 ? "9+" : unread}</span>
          )}
        </DropdownToggle>
        <DropdownMenu end className="header-notif__menu">
          <div className="header-notif__header flex items-center justify-between gap-2">
            <span>{t("Novedades")}</span>
            <BrandLine brand={feed.brand} />
          </div>

          {feed.status === "loading" && feed.items.length === 0 ? (
            <div className="flex flex-col gap-2 p-3" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-md bg-bg-muted" />
              ))}
            </div>
          ) : feed.status === "error" && feed.items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-4 text-center text-sm text-fg-muted">
              <p>{t("No se pudieron cargar las novedades.")}</p>
              <Button size="sm" variant="outline" onClick={() => loadPage(1)}>
                {t("Reintentar")}
              </Button>
            </div>
          ) : feed.items.length === 0 ? (
            <div className="header-notif__empty">
              <span className="material-symbols-outlined" aria-hidden="true">
                notifications_off
              </span>
              <p>{t("No hay novedades por ahora")}</p>
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <ul className="flex flex-col py-1">
                {feed.items.map((news) => (
                  <li key={news.id}>
                    <button
                      type="button"
                      onClick={() => openDetail(news)}
                      className={cn(
                        "flex w-full flex-col gap-1 px-3 py-2 text-left hover:bg-bg-muted",
                        "focus:outline-none focus-visible:bg-bg-muted",
                      )}
                    >
                      <span className="flex items-center gap-2 text-xs text-fg-muted">
                        <CategoryBadge category={news.category} />
                        <time dateTime={news.date}>{formatNewsDate(news.date, i18n.language)}</time>
                        {!news.read && (
                          <span className="ml-auto flex items-center gap-1 font-medium text-brand-600">
                            <span className="h-2 w-2 rounded-full bg-brand-500" aria-hidden="true" />
                            {t("Nueva")}
                          </span>
                        )}
                      </span>
                      <span className="text-sm font-medium text-fg">{news.title}</span>
                      {news.summary && (
                        <span className="line-clamp-2 text-xs text-fg-muted">{news.summary}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {feed.items.length < feed.total && (
                <div className="border-t border-border p-2 text-center">
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={feed.status === "loading"}
                    onClick={() => loadPage(feed.page + 1)}
                  >
                    {t("Ver más")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DropdownMenu>
      </Dropdown>

      <NewsDetail news={detail} onClose={() => setDetail(null)} onCta={onCta} onNavigate={goTo} />
    </>
  );
}
